import { Component, OnInit, ViewChild, inject } from '@angular/core';
import {
    WorkflowForm,
    WorkflowFormComponent,
    WorkflowFormKey,
    WorkflowTaskDataService,
    WorkflowTaskDefinitionKey,
} from '@lib/workflow-integration';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    IPurchaseRequisitionTaskPreview,
    PurchaseRequisitionTaskPreview,
} from '../../types/PurchaseRequisitionTaskPreview';
import { IPurchaseRequisitionTask, PurchaseRequisitionTask } from '../../types/PurchaseRequisitionTask';
import { DemandDataService } from '../../../demand/demand-data.service';
import {
    createDeeplinkCompletionContext,
    CustomRouter,
    DeeplinkCompletionAction,
    DeeplinkCompletionContextService,
    NotificationMessageLevel,
    NotificationService,
} from '@lib/core';
import { PurchaseRequisitionApproveDeclineReworkCompletionData } from './types/PurchaseRequisitionApproveDeclineReworkCompletionData';
import { ExplanationDialogService, IExplanationDialogResult } from '@lib/util';
import { PurchaseRequisitionDetailComponent } from '../../../purchase-requisition/purchase-requisition-detail/purchase-requisition-detail.component';
import { ActivatedRoute } from '@angular/router';
import { CommentDataService } from '@lib/comment';
import { ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import { I18nService } from '@lib/i18n';
import { HttpErrorResponse } from '@angular/common/http';
import { ValidationSubject } from '../../../demand/validation/types/IValidationError';
import { DemandValidationError, IDemandValidationError } from '../../../demand/validation/types/IDemandValidationError';
import {
    AccountingValidationError,
    IAccountingValidationError,
} from '../../../demand/validation/types/IAccountingValidationError';
import { PurchaseRequisitionDataService } from '../../../purchase-requisition/purchase-requisition-data.service';
import { PurchaseRequisitionResult } from '../../../purchase-requisition/types/PurchaseRequisitionResult';
import { IPurchaseRequisitionCompanyCodeValidationResponse } from '../../../purchase-requisition/types/IPurchaseRequisitionCompanyCodeValidationResponse';
import { firstValueFrom } from 'rxjs';

@WorkflowForm(
    marker('Bedarfsmeldung freigeben'),
    [WorkflowFormKey.PURCHASE_REQUISITION_APPROVE_OR_REWORK_FORM],
    (task: IPurchaseRequisitionTaskPreview, displayName: string) =>
        new PurchaseRequisitionTaskPreview(task, displayName),
    marker('Bedarfsmeldung freigeben'),
)
@Component({
    selector: 'approve-decline-rework-task',
    templateUrl: './approve-decline-rework-task.component.html',
    styleUrls: ['./approve-decline-rework-task.component.scss'],
    standalone: false,
})
export class ApproveDeclineReworkTaskComponent extends WorkflowFormComponent implements OnInit {
    private readonly taskDataService = inject(WorkflowTaskDataService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly commentDataService = inject(CommentDataService);
    private readonly itemClassificationDataService = inject(ItemClassificationDataService);
    private readonly fileDataService = inject(FileDataService);
    private readonly router = inject(CustomRouter);
    private readonly explanationDialog = inject(ExplanationDialogService);
    private readonly route = inject(ActivatedRoute);
    private readonly i18n = inject(I18nService);
    private readonly deeplinkCompletionContext = inject(DeeplinkCompletionContextService);
    private readonly purchaseRequisitionDataService = inject(PurchaseRequisitionDataService);
    private readonly notificationService = inject(NotificationService);

    @ViewChild('purchaseRequisitionDetailComponent')
    purchaseRequisitionDetailComponent?: PurchaseRequisitionDetailComponent;
    public task?: PurchaseRequisitionTask;

    public static readonly validatePurchaseRequisitionFor = [
        WorkflowTaskDefinitionKey.PURCHASE_REQUISITION_BUYER_APPROVER,
        WorkflowTaskDefinitionKey.PURCHASE_REQUISITION_CUSTOM_ACCOUNT_MANAGER_APPROVER,
    ];
    private explanation = '';
    private hasCompanyCodeValidationError = false;

    public get needsValidation(): boolean {
        return (
            !!this.task?.taskDefinitionKey &&
            ApproveDeclineReworkTaskComponent.validatePurchaseRequisitionFor.includes(this.task.taskDefinitionKey)
        );
    }

    /**
     * Whether a supplier must be selected before the purchase requisition can be approved.
     * Either implied by the task definition key (legacy buyer/account-manager approvers) or
     * driven by the `requireValidSuppliers` task input parameter set on individual user tasks
     * in the BPMN (e.g. eproc-purchase-requisition-release-with-company-codes.bpmn).
     */
    public get needsSupplier(): boolean {
        return this.needsValidation || !!this.task?.requireValidSuppliers;
    }

    /**
     * Whether the selected suppliers match the purchase requisition's result document type.
     * Only enforced when a supplier is required: an order needs exactly one supplier, an RFQ
     * accepts one or more. Drives the approve button so the demand-content validator (which only
     * enforces "at least one") cannot be bypassed for orders.
     */
    public get suppliersValidForResult(): boolean {
        if (!this.needsSupplier || !this.task) {
            return true;
        }
        const supplierCount = this.task.purchaseRequisition.demand.suppliers.length;
        return this.task.purchaseRequisition.resultsIn === PurchaseRequisitionResult.RFQ
            ? supplierCount >= 1
            : supplierCount === 1;
    }

    /**
     * Inline hint shown above the approve button when the selected suppliers do not match the
     * result document type. Covers both the missing-supplier and the over-selection case so the
     * approver sees why the approve button is disabled (the button is gated on
     * suppliersValidForResult). Returns null when the supplier selection is valid or not required.
     */
    public get supplierHint(): string | null {
        if (this.suppliersValidForResult || !this.task) {
            return null;
        }
        return this.task.purchaseRequisition.resultsIn === PurchaseRequisitionResult.RFQ
            ? marker('Es muss mindestens ein Lieferant ausgewählt sein!')
            : marker('Es muss genau ein Lieferant ausgewählt sein!');
    }

    /**
     * EPROC-11425 reopen #5b (238308): the demand-invalid branch of approvalBlocked carries no
     * hint of its own — the approver saw a dead release button and nothing else. The supplier
     * hint above explains its own case, so it takes precedence and this one stays quiet then.
     */
    public get showDemandInvalidHint(): boolean {
        return !this.isCompleted && !this.supplierHint && !this.purchaseRequisitionDetailComponent?.isValid;
    }

    public get approvalBlocked(): boolean {
        return !this.purchaseRequisitionDetailComponent?.isValid || !this.suppliersValidForResult || this.isCompleted;
    }

    public approve(): void {
        if (!this.approvalBlocked) {
            this._isCompleted = true;
            this.complete(true);
        }
    }

    public decline(): void {
        this.explanationDialog.open(this.explanation, true).subscribe((result: IExplanationDialogResult) => {
            this.explanation = result.explanation;
            if (!result.cancelled) {
                this.complete(false, false, result.explanation);
            }
        });
    }

    public sendToRework(): void {
        this.explanationDialog.open(this.explanation, true).subscribe((result: IExplanationDialogResult) => {
            this.explanation = result.explanation;
            if (!result.cancelled) {
                this.complete(false, true, result.explanation);
            }
        });
    }

    ngOnInit(): void {
        const taskId = this.taskId ? this.taskId : this.route.snapshot.params['taskId'];
        if (taskId) {
            this.getTask(taskId);
        }
    }

    public getTask(taskId: string): void {
        this.taskDataService
            .getSingleTaskById<IPurchaseRequisitionTask>(taskId)
            .subscribe((task: IPurchaseRequisitionTask) => {
                this.task = new PurchaseRequisitionTask(
                    task,
                    this.demandDataService,
                    this.commentDataService,
                    this.itemClassificationDataService,
                    this.fileDataService,
                    this.i18n,
                );
            });
    }

    private async complete(isApproved = false, rework?: boolean, explanation?: string): Promise<void> {
        if (this.task) {
            await this.task.purchaseRequisition.demand.saveMetaData();
            if (isApproved && this.needsValidation) {
                const isCompanyCodeValid = await this.validateCompanyCode();
                if (!isCompanyCodeValid) {
                    this._isCompleted = false;
                    return;
                }
            }
            this.taskDataService
                .completeTask(
                    this.task.id,
                    new PurchaseRequisitionApproveDeclineReworkCompletionData(isApproved, rework, explanation),
                )
                .subscribe({
                    next: () => {
                        this.deeplinkCompletionContext.completeRun(
                            createDeeplinkCompletionContext(
                                isApproved
                                    ? DeeplinkCompletionAction.PURCHASE_REQUISITION_APPROVED
                                    : rework
                                      ? DeeplinkCompletionAction.PURCHASE_REQUISITION_SENT_TO_REWORK
                                      : DeeplinkCompletionAction.PURCHASE_REQUISITION_REJECTED,
                                this.task!.purchaseRequisition.number,
                            ),
                        );
                        this.completed.emit();
                        if (this.routeToListAfterCompletion) {
                            this.router.navigate([this.router.getViewFromRoute(), 'task', 'list'], {
                                replaceUrl: true,
                            });
                        }
                    },
                    error: (error: HttpErrorResponse) => {
                        this._isCompleted = false;
                        const validationError: IDemandValidationError | undefined = error?.error?.error;
                        if (
                            validationError &&
                            validationError.subject === ValidationSubject.DEMAND &&
                            this.task?.purchaseRequisition
                        ) {
                            this.task.purchaseRequisition.demand.setValidationError(
                                new DemandValidationError(validationError, this.i18n),
                            );
                        }
                    },
                });
        }
    }

    private async validateCompanyCode(): Promise<boolean> {
        if (!this.task?.purchaseRequisition?.id) {
            return true;
        }

        try {
            const response = await firstValueFrom(
                this.purchaseRequisitionDataService.validateCompanyCode(this.task.purchaseRequisition.id),
            );
            return this.handleCompanyCodeValidationResponse(response);
        } catch {
            this.notificationService.showNotification(
                marker('Kontierungsprüfung für den Buchungskreis konnte nicht durchgeführt werden.'),
                NotificationMessageLevel.ERROR,
            );
            return false;
        }
    }

    private handleCompanyCodeValidationResponse(response: IPurchaseRequisitionCompanyCodeValidationResponse): boolean {
        if (response.companyCodeMissing) {
            this.applyCompanyCodeMissingError();
            this.notificationService.showNotification(
                marker('Buchungskreis muss gesetzt sein, bevor die Bedarfsmeldung freigegeben werden kann.'),
                NotificationMessageLevel.ERROR,
            );
            return false;
        }

        if (!response.errors?.length) {
            this.clearCompanyCodeValidationError();
            return true;
        }

        const message = this.buildCompanyCodeValidationMessage(response.errors);
        if (response.blocking) {
            this.applyCompanyCodeValidationError(response.errors);
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR, undefined, true);
            return false;
        }

        this.notificationService.showNotification(message, NotificationMessageLevel.WARNING, undefined, true);
        return true;
    }

    private buildCompanyCodeValidationMessage(errors: IAccountingValidationError[]): string {
        return errors.map((error) => new AccountingValidationError(error, this.i18n).message).join('\n');
    }

    private applyCompanyCodeMissingError(): void {
        if (!this.task?.purchaseRequisition?.demand) {
            return;
        }

        const message = this.i18n.translateInstant(
            marker('Buchungskreis muss gesetzt sein, bevor die Bedarfsmeldung freigegeben werden kann.'),
        );
        const dto: IDemandValidationError = {
            demandId: this.task.purchaseRequisition.demand.id,
            otherErrors: true,
            otherErrorsMessage: message,
            extensionErrors: [],
            subject: ValidationSubject.DEMAND,
        };
        this.task.purchaseRequisition.demand.setValidationError(new DemandValidationError(dto, this.i18n));
        this.hasCompanyCodeValidationError = true;
    }

    private applyCompanyCodeValidationError(errors: IAccountingValidationError[]): void {
        if (!this.task?.purchaseRequisition?.demand) {
            return;
        }

        const dto: IDemandValidationError = {
            demandId: this.task.purchaseRequisition.demand.id,
            otherErrors: false,
            otherErrorsMessage: '',
            extensionErrors: errors,
            subject: ValidationSubject.DEMAND,
        };
        this.task.purchaseRequisition.demand.setValidationError(new DemandValidationError(dto, this.i18n));
        this.hasCompanyCodeValidationError = true;
    }

    private clearCompanyCodeValidationError(): void {
        if (this.hasCompanyCodeValidationError && this.task?.purchaseRequisition?.demand) {
            this.task.purchaseRequisition.demand.validationError = undefined;
            this.hasCompanyCodeValidationError = false;
        }
    }
}
